/* eslint-disable max-len */
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { LineGraph } from "@similarweb/pro-logic-components";
import { TabList, Tabs } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import { chosenSites } from "app/common/services/chosenSites";
import { useBacklinksTimeseriesBulkApi } from "app/pages/digital-marketing/backlinks/shared/backlinks-api-service";
import {
    currentGranularityAllowedInRange,
    filterDataFromDate,
    getExcelDownloadURL,
    getGranularityOptionsWithState,
    numFilter,
    useExpectedBacklinksParams,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { Spinner } from "app/pages/digital-marketing/backlinks/shared/components/Spinner";
import { allTrackers } from "app/services/track/track";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import {
    BacklinksChartBoxContainer,
    BacklinksChartContainer,
    OvertimeChartSummaryRowContainer,
    StyledTab,
} from "app/pages/digital-marketing/backlinks/reports/overview/BacklinksOverviewStyledComponents";
import { BacklinksReferringOvertimeSummaryRow } from "app/pages/digital-marketing/backlinks/reports/overview/components/BacklinksOvertimeChart/BacklinksReferringOvertimeSummaryRow";
import {
    API_DATE_REQUEST_FORMAT,
    chartTimeGranularityOptions,
    chartTimeRangeOptions,
    GRANULARITY_INDEX,
    START_DATE_13_M,
    TIME_RANGES_INDEX,
    TODAY,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksConsts";
import { getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { CoreInlineWebsiteQueryBar } from "../compare/CoreInlineWebsiteQueryBar";
import type { WebsitePageResults } from "../compare/types";
import type { GetPrimarySiteReturnType } from "../compare/utils";

import { getOverTimeChartConfig } from "./BacklinksReferringOvertimeChartUtils";
import {
    transformBacklinksData,
    formatBacklinksChartData,
    compareModeSelectedTabsConfig,
} from "./utils/dataTransformUtils";
import { filterDataByGranularity } from "./utils/granularityUtils";

type ValueOf<T> = T[keyof T];

const CompareModeTabs = {
    REFERRING_DOMAINS: 0,
    BACKLINKS: 1,
} as const;

export const BacklinksReferringOvertimeChart: FC = () => {
    const params = useExpectedBacklinksParams();
    const dispatch = useDispatch();
    const translate = useTranslation();

    const [granularityIndex, setGranularityIndex] = useState<ValueOf<typeof GRANULARITY_INDEX>>(
        GRANULARITY_INDEX.MONTHLY,
    );
    const [visibleItems, setVisibleItems] = useState<Record<string, boolean>>({});
    const [rangeIndex, setRangeIndex] = useState<ValueOf<typeof TIME_RANGES_INDEX>>(TIME_RANGES_INDEX.MONTH_13);
    const [competitors, setCompetitors] = useState<WebsitePageResults>([]);
    const [compareModeTab, setCompareModeTab] = useState<ValueOf<typeof CompareModeTabs>>(
        CompareModeTabs.REFERRING_DOMAINS,
    );

    const keysToFetch = [params.key].concat(competitors.map((item) => item.name)).filter(Boolean) as string[];

    const { data = [], isLoading } = useBacklinksTimeseriesBulkApi(keysToFetch, {
        from: START_DATE_13_M.format(API_DATE_REQUEST_FORMAT),
        to: TODAY.format(API_DATE_REQUEST_FORMAT),
        isDaily: true,
    });
    const isCompareMode = data.length > 1;
    const excelDownloadURL = getExcelDownloadURL("timeseriesExcel", params);

    useEffect(() => {
        if (!currentGranularityAllowedInRange({ granularityIndex, rangeIndex })) {
            // Weekly is the value that is enabled for all ranges
            setGranularityIndex(GRANULARITY_INDEX.WEEKLY);
        }
    }, [rangeIndex, granularityIndex]);

    const availableChartTimeGranularityOptions = useMemo(() => {
        return getGranularityOptionsWithState({ currentRangeIndex: rangeIndex });
    }, [rangeIndex]);

    const rangeFilteredData = useMemo(() => {
        return data.map((dataItem) => filterDataFromDate(rangeIndex, dataItem, granularityIndex));
    }, [data, granularityIndex, rangeIndex]);

    const granularityFilteredData = useMemo(() => {
        return filterDataByGranularity(rangeFilteredData, granularityIndex);
    }, [rangeFilteredData, granularityIndex]);

    const calculatedData = useMemo(() => {
        return transformBacklinksData(granularityFilteredData);
    }, [granularityFilteredData]);

    const graphData = useMemo(() => {
        return formatBacklinksChartData(
            calculatedData,
            isCompareMode,
            compareModeTab,
            granularityIndex,
            keysToFetch,
            visibleItems,
        );
    }, [calculatedData, compareModeTab, granularityIndex, isCompareMode, keysToFetch, visibleItems]);

    // Update granularity index
    const granularityUpdate = (newGranularityIndex: ValueOf<typeof GRANULARITY_INDEX>) => {
        allTrackers.dispatchTrackEvent(
            "Time Granularity",
            "Click",
            [
                "Domains and Backlinks",
                chartTimeGranularityOptions[newGranularityIndex].value,
                chartTimeRangeOptions[granularityIndex].value,
            ].join("/"),
        );
        setGranularityIndex(newGranularityIndex);
    };

    // Update range index
    const rangeUpdate = (newRangeIndex: number) => {
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "Click",
            [
                "Domains and Backlinks",
                chartTimeRangeOptions[newRangeIndex].value,
                chartTimeGranularityOptions[granularityIndex].value,
            ].join("/"),
        );
        setRangeIndex(newRangeIndex);
    };

    const graphConfig = useMemo(() => {
        return getOverTimeChartConfig(chartTimeGranularityOptions[granularityIndex].value, isCompareMode);
    }, [granularityIndex, isCompareMode]);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "search.backlinks.overview.overtime_chart.excel.click",
            "click",
            "search.backlinks.overview.overtime_chart.excel.click",
        );
    };
    const primaryWebSite = useMemo((): GetPrimarySiteReturnType => {
        const { domain } = params.key ? getUrlComponents(params.key) : { domain: "" };

        const icon = (chosenSites.listInfo as Record<string, { icon: string }>)[domain]?.icon ?? "";

        return {
            url: params.key ?? "",
            searchType: "domain",
            icon,
            domain,
        };
    }, [params.key]);

    const handleCompetitorsChange = (newCompetitors: WebsitePageResults) => {
        setCompetitors(newCompetitors);
    };

    const handleSetCompareModeTab = (index: ValueOf<typeof CompareModeTabs>) => {
        setCompareModeTab(index);
        allTrackers.dispatchTrackEvent(
            "Referring Domains and Backlinks",
            "Click",
            [
                "Domains and Backlinks",
                index === CompareModeTabs.REFERRING_DOMAINS ? "Referring Domains" : "Backlinks",
                chartTimeGranularityOptions[granularityIndex].value,
            ].join("/"),
        );
    };

    const handleItemVisibility = useCallback((item: string) => {
        setVisibleItems((prevState) => {
            return {
                ...prevState,
                [item]: prevState[item] === undefined ? false : !prevState[item],
            };
        });
    }, []);

    const graphKey = useMemo(() => {
        return `${graphData.map((item) => item.name).join(",")}-${granularityIndex}-${compareModeTab}-${JSON.stringify(visibleItems)}`;
    }, [graphData, granularityIndex, compareModeTab, visibleItems]);

    return (
        <BacklinksChartBoxContainer data-automation="time-series-chart">
            <WidgetContainer
                isLoading={data.length ? undefined : isLoading}
                title={translate("digitalsuite.backlinks.overview.overtime_chart.title")}
                noData={!calculatedData?.length}
                loaderWidth="100%"
                height="550px"
                headerHeight={82}
                dynamicSwitcherConfig={[
                    {
                        items: chartTimeRangeOptions,
                        onItemClick: rangeUpdate,
                        initialSelectedItem: rangeIndex,
                    },
                    {
                        items: availableChartTimeGranularityOptions,
                        onItemClick: granularityUpdate,
                        initialSelectedItem: granularityIndex,
                    },
                ]}
                excelUrl={isCompareMode ? undefined : excelDownloadURL}
                onExcelClick={handleExcelClick}
                withBorder={true}
                downloadProps={{
                    showToast: () => dispatch(showErrorToast(translate("global.download.excel.error"))),
                }}
            >
                <OvertimeChartSummaryRowContainer>
                    <CoreInlineWebsiteQueryBar
                        primarySite={primaryWebSite}
                        initialCompetitors={competitors}
                        onCompetitorsChange={handleCompetitorsChange}
                        maxAllowedCompetitors={3}
                    />
                </OvertimeChartSummaryRowContainer>
                {isCompareMode ? (
                    <Tabs
                        selectedIndex={compareModeTab}
                        onSelect={handleSetCompareModeTab}
                    >
                        <TabList>
                            {compareModeSelectedTabsConfig.map(
                                (tab: (typeof compareModeSelectedTabsConfig)[number]) => (
                                    <StyledTab
                                        key={tab.dataAutomation}
                                        data-automation={tab.dataAutomation}
                                    >
                                        {translate(tab.tabNameKey)}
                                    </StyledTab>
                                ),
                            )}
                        </TabList>
                    </Tabs>
                ) : (
                    <BacklinksReferringOvertimeSummaryRow />
                )}
                <BacklinksChartContainer>
                    {isLoading ? (
                        <Flex
                            height="100%"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Spinner size={100} />
                        </Flex>
                    ) : (
                        <LineGraph
                            key={graphKey}
                            // @ts-expect-error FIXME: Types of property 'zones' are incompatible
                            data={graphData}
                            graphConfig={graphConfig}
                            customLegendTrackingHandler={handleItemVisibility}
                            shouldPresentLegend={isCompareMode}
                            labelsAreRotated={false}
                            legendPosition={isCompareMode ? "right" : undefined}
                            legendsMaxWidth={isCompareMode ? 150 : undefined}
                            legendHeaders={isCompareMode ? ["Domain", "Total"] : undefined}
                            isToggleAllEnabled
                            shouldPresentValueAtLegend={true}
                            legendsHeight="100%"
                            legendValueFilter={(value) => numFilter(value).toString()}
                        />
                    )}
                </BacklinksChartContainer>
            </WidgetContainer>
        </BacklinksChartBoxContainer>
    );
};
